import moment from "moment";
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { calculateRoundedValue, imageRender, numberFormat, thousandSeperate } from "../../../helpers";
import BillDegitalLogicstics from "../../../components/bill.orderGroups/BillDegitalLogicstics";
import { useLazyQuery, useMutation } from "@apollo/client";
import { ORDER_DELIVERY_DETAIL } from "../../../graphql/orderDelivery/query";
import { GET_PRESIGN_URL } from "../../../graphql/presignUrl/query";
import { SEND_LOGISTIC_BILL_IMAGE } from "../../../graphql/logistic/mutation";
import axios from "axios";
import _ from "lodash";
import { FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import "./checkout-theme.css";

const FinishOrder = ({ orderData }) => {
  const [dataTracking, setDataTracking] = useState();

  // ປັດຂຶ້ນເປັນຫຼັກພັນ ຄືກັບທີ່ແຖບລຸ່ມ ແລະ ໜ້າຢືນຢັນໃຊ້
  const sumPriceRaw = useMemo(
    () => calculateRoundedValue((orderData?.sumPrice ?? 0) / 1000) * 1000,
    [orderData]
  );

  // "ລວມເງິນກີບ" ຊ້ຳກັບ "ເງິນທີ່ຊຳລະຕົວຈິງ" ຕອນບໍ່ມີສະກຸນອື່ນ ແລະ ບໍ່ມີການປັດເສດ
  // ສະແດງ 2 ແຖວຄ່າດຽວກັນເຮັດໃຫ້ລູກຄ້າສົງໄສວ່າຈ່າຍ 2 ເທື່ອ
  const hasBreakdown = useMemo(
    () =>
      orderData?.sumPriceBaht > 0 ||
      orderData?.sumPriceUsd > 0 ||
      Number(orderData?.totalPrice ?? 0) !== sumPriceRaw,
    [orderData, sumPriceRaw]
  );

  // COD ທີ່ຮ້ານຍັງບໍ່ປິດອໍເດີ — ເງິນຍັງບໍ່ເຂົ້າ ຈຶ່ງບໍ່ຄວນຂຶ້ນ "ຊຳລະສຳເລັດ"
  const isCodPending =
    !_.isEmpty(orderData?.dataConfirmCod) && orderData?.status !== "COMPLETED";

  const [getOrderDeliveryDetail, { data: orderDeliveryDetail, loading: loadingOrderDelivery }] =
    useLazyQuery(ORDER_DELIVERY_DETAIL, { fetchPolicy: "cache-and-network" });

  useEffect(() => {
    if (orderData?.isPaymented) {
      fetchOrderDetailOfDelivery(orderData?.id);
    }
  }, [orderData]);

  useEffect(() => {
    if (orderDeliveryDetail) {
      setDataTracking(orderDeliveryDetail?.orderDeliveryOrderGroupId?.data);
    }
  }, [orderDeliveryDetail]);

  // fetch order delivery detail
  /**
   * Send the waybill into the customer's Messenger chat as a real image.
   *
   * This page is the right place because the customer is already here: the label is
   * rendered on screen from orderDelivery.deliveryTackingNo, so the image they receive is
   * the one they can see. No second renderer, no server-side headless browser.
   *
   * Entirely best-effort — every failure is swallowed. The customer still has the bill on
   * screen and the download button, and nothing here may disturb the order.
   */
  const sentFor = useRef(null);
  const [getPresignUrl] = useLazyQuery(GET_PRESIGN_URL, { fetchPolicy: "no-cache" });
  const [sendLogisticBillImage] = useMutation(SEND_LOGISTIC_BILL_IMAGE);

  const handleBillImageReady = useCallback(
    async (dataUrl, trackingNo) => {
      try {
        if (!orderData?.id || !dataUrl || sentFor.current === trackingNo) return;
        sentFor.current = trackingNo; // set before awaiting, so a re-render cannot double-send

        const blob = await (await fetch(dataUrl)).blob();
        const name = `waybill-${orderData.id}-${trackingNo}.jpg`;

        // contentType is signed AND stored; Meta decides from it whether to render the
        // image, and a PUT whose header differs from what was signed is rejected 403.
        const { data } = await getPresignUrl({
          variables: { name, contentType: "image/jpeg" },
        });
        const uploadUrl = data?.preSignedUrl?.url;
        if (!uploadUrl) return;

        await axios.put(uploadUrl, blob, {
          headers: { "Content-Type": "image/jpeg" },
        });

        // The presigned PUT url and the public GET url are the same object; the query
        // string is only the signature. Objects are written ACL public-read, which is what
        // lets Meta fetch it — a presigned GET would expire.
        const publicUrl = uploadUrl.split("?")[0];

        await sendLogisticBillImage({
          variables: { orderGroupId: orderData.id, imageUrl: publicUrl },
        });
      } catch (error) {
        sentFor.current = null; // allow a later attempt
        console.error("send waybill image failed:", error);
      }
    },
    [orderData?.id, getPresignUrl, sendLogisticBillImage]
  );

  const fetchOrderDetailOfDelivery = async (id) => {
    try {
      if (loadingOrderDelivery) return;
      await getOrderDeliveryDetail({
        variables: {
          where: {
            orderGroupId: id,
          },
        },
      });
    } catch (error) {
      console.error({ error });
    }
  };

  return (
    <div style={{ display: "flex", width: "100%", flexDirection: "column", gap: 12 }}>
      <BillDegitalLogicstics dataTracking={dataTracking} loadingOrderDelivery={loadingOrderDelivery} onImageReady={handleBillImageReady} />

      {isCodPending && (
        <div className="cf-banner cf-banner--warn">
          <FaInfoCircle className="cf-banner__icon" aria-hidden="true" />
          <span>ອໍເດີນີ້ເກັບເງິນປາຍທາງ (COD) — ຈ່າຍຕອນຮັບເຄື່ອງ</span>
        </div>
      )}

      <div className="cf-card cf-rows">
        <div className="cf-row">
          <span className="cf-row__label">ສະຖານະ (Status)</span>
          <span
            className={`cf-row__value ${orderData?.isPaymented ? "cf-row__value--paid" : "cf-row__value--unpaid"}`}
          >
            <span className="cf-status-dot" aria-hidden="true" />
            {orderData?.isPaymented ? "ຈ່າຍເງິນແລ້ວ" : "ຍັງບໍ່ຈ່າຍເງິນ"}
          </span>
        </div>
        <div className="cf-row">
          <span className="cf-row__label">ຊື່ລູກຄ້າ (Name)</span>
          <span className="cf-row__value">
            {dataTracking?.receiver?.fullName ?? orderData?.customerName ?? orderData?.cfName}
          </span>
        </div>
        <div className="cf-row">
          <span className="cf-row__label">ເບີໂທ (Phone)</span>
          <span className="cf-row__value">{orderData?.phone}</span>
        </div>
        <div className="cf-row">
          <span className="cf-row__label">ເລກທີບິນ (Invoice)</span>
          <span className="cf-row__value">{orderData?.code}</span>
        </div>
        <div className="cf-row">
          <span className="cf-row__label">ວັນທີ່ສັ່ງຊື້ (Date)</span>
          <span className="cf-row__value">
            {moment(orderData?.createdAt).format("DD/MM/YYYY HH:mm")}
          </span>
        </div>
        {/* ມີເລກ tracking ແລ້ວ ຂໍ້ມູນຂົນສົ່ງຄົບກວ່າຢູ່ໃບ tracking ຂ້າງເທິງ ບໍ່ຕ້ອງຂຽນຊ້ຳ
            ແຍກເປັນ 2 ແຖວ — ຂອງເກົ່າໃຊ້ `logistic ?? addressCf` ເຊິ່ງຕົກໄປ addressCf
            ສະເພາະຕອນ logistic ເປັນ null. ໂໝດພິມທີ່ຢູ່ເອງມັນເປັນຄ່າຫວ່າງ "" ບໍ່ແມ່ນ null
            ຜົນຄື ແຖວນີ້ບອດ ທີ່ຢູ່ທີ່ລູກຄ້າພິມບໍ່ຂຶ້ນຈັກບ່ອນ */}
        {!dataTracking?.deliveryTackingNo && !!orderData?.logistic && (
          <div className="cf-row">
            <span className="cf-row__label">ຂົນສົ່ງ (Delivery)</span>
            <span className="cf-row__value">{orderData?.logistic}</span>
          </div>
        )}
        {!dataTracking?.deliveryTackingNo && !!orderData?.addressCf && (
          <div className="cf-row">
            <span className="cf-row__label">ທີ່ຢູ່ຈັດສົ່ງ (Address)</span>
            <span className="cf-row__value">{orderData?.addressCf}</span>
          </div>
        )}
      </div>

      <div className="cf-section-label">ລາຍການສິນຄ້າ (Order Items)</div>

      <div className="cf-items">
        {orderData?.orders?.map((order, index) => (
          <div className="cf-item" key={order?.id ?? index}>
            <img
              className="cf-item__thumb"
              alt=""
              width={48}
              height={48}
              src={imageRender(
                order?.liveStock?.stock?.image?.[0] ??
                  order?.postStock?.stock?.image?.[0] ??
                  order?.liveStock?.stock?.image ??
                  order?.postStock?.stock?.image
              )}
              // ບໍ່ມີຮູບໃຫ້ໂຫຼດ ຈະຂຶ້ນໄອຄອນຮູບແຕກ — ປ່ຽນເປັນຈຸດໃສ ເຫຼືອແຕ່ພື້ນເທົາຂອງ .cf-item__thumb
              // ລ້າງ onerror ກ່ອນ ບໍ່ດັ່ງນັ້ນຖ້າ src ໃໝ່ພັງນຳຈະວົນ
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
              }}
            />
            <div className="cf-item__body">
              <div className="cf-item__name">
                {order?.productName}
                {order?.cfMessage ? ` (${order?.cfMessage})` : ""}
              </div>
              <div className="cf-item__meta">
                {thousandSeperate(order?.price)} {order?.currency} x{order?.amount}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="cf-section-label">ຍອດລວມທັງໝົດ (Total)</div>

      <div className="cf-card cf-rows">
        <div className="cf-row">
          <span className="cf-row__label">ລວມຈຳນວນ</span>
          <span className="cf-row__value">{thousandSeperate(orderData?.amount)}</span>
        </div>
        {hasBreakdown && (
          <div className="cf-row">
            <span className="cf-row__label">ລວມເງິນກີບ</span>
            <span className="cf-row__value">₭{thousandSeperate(orderData?.totalPrice)}</span>
          </div>
        )}
        {orderData?.sumPriceBaht > 0 && (
          <div className="cf-row">
            <span className="cf-row__label">ລວມເງິນບາດ</span>
            <span className="cf-row__value">฿{thousandSeperate(orderData?.sumPriceBaht)}</span>
          </div>
        )}
        {orderData?.sumPriceUsd > 0 && (
          <div className="cf-row">
            <span className="cf-row__label">ລວມເງິນໂດລາ</span>
            <span className="cf-row__value">${thousandSeperate(orderData?.sumPriceUsd)}</span>
          </div>
        )}
        <div className="cf-row cf-total--grand">
          <span className="cf-row__label">ເງິນທີ່ຊຳລະຕົວຈິງ</span>
          <span className="cf-row__value">{numberFormat(sumPriceRaw)}₭</span>
        </div>
      </div>

      {isCodPending ? (
        <div className="cf-pay-note">ອໍເດີນີ້ຍັງບໍ່ຈ່າຍເງິນ ເນື່ອງຈາກເກັບປາຍທາງ</div>
      ) : orderData?.paySlip ? (
        <>
          <div className="cf-section-label">ຮູບຊຳລະ (Payment Slip)</div>
          <div className="cf-card">
            <img className="cf-slip" alt="ຮູບຫຼັກຖານການໂອນເງິນ" src={imageRender(orderData?.paySlip)} />
          </div>
        </>
      ) : (
        // ຂອງເກົ່າດຶງ GIF ຈາກ server ທະນາຄານອິນໂດເນເຊຍ (app.bestportal.bri.co.id)
        // ໜ້າ "ຊຳລະສຳເລັດ" ບໍ່ຄວນຂຶ້ນກັບ asset ຂອງຄົນອື່ນ — ລາວປິດເມື່ອໃດກໍ່ພັງ ແລະ ຮົ່ວ referrer ອອກໄປ
        <div className="cf-banner cf-banner--success">
          <FaCheckCircle className="cf-banner__icon" aria-hidden="true" />
          <span>ຊຳລະເງິນສຳເລັດແລ້ວ</span>
        </div>
      )}
    </div>
  );
};

export default FinishOrder;
